/**
 * A figure's page (docs/SPEC.md §P29.2 (7); P29-T06): the People tab's
 * card opens it. A figure is a stylised body a film's people are drawn as;
 * the page shows its picture, its name, the words it is chosen by, its six
 * colours and what it can do in a film. There is no 3D view: the viewer has
 * no skinning, and the film plays a figure, not this page.
 */
import { useParams } from "react-router-dom";
import type { AssetDetail } from "../api/types";
import { andList } from "../copy/cast";
import { figureHeight } from "./AssetCard";
import {
  INSPECTOR_GRID,
  Inspector,
  InspectorHeader,
  UsedInAds,
} from "./inspector";

/** What the page says of every figure. */
export const FIGURE_SENTENCE =
  "A stylised figure for films. Its colours can follow a brand.";
/** The six slots of a figure, in their order, in the person's words. */
export const SLOT_WORDS: Record<string, string> = {
  skin: "Skin",
  face: "Face",
  hair: "Hair",
  top: "Top",
  bottom: "Bottoms",
  shoes: "Shoes",
};
/** What every figure can do in a film (the nine actions, in words; every
 *  figure stands on the same skeleton, so every one plays them all). */
export const ACTION_WORDS = [
  "stand",
  "walk",
  "run",
  "sit down",
  "sit",
  "stand up",
  "pedal",
  "wave",
  "present",
];
export const ACTIONS_LINE = `In a film it can ${andList(ACTION_WORDS)}.`;
/** What a figure can hold (P30, §P30.2 (7)). */
export const HOLDS_LINE =
  "It can hold things: a cup, a phone, a remote, a box.";
export const COLOURS_HELP =
  "Hair, top, bottoms and shoes can take your brand's colours; skin and face keep theirs.";

interface Slot {
  name: string;
  default?: { plain?: { color_hex?: string } };
}

/** A slot's colour as its document gives it (`#rrggbb`), or null. */
function colourOf(slot: Slot): string | null {
  const hex = slot.default?.plain?.color_hex;
  return typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : null;
}

function FigureInspector({ detail }: { detail: AssetDetail }) {
  const meta = detail.meta;
  const name = String(meta.name ?? "");
  const words: string[] = Array.isArray(meta.words) ? meta.words : [];
  const slots: Slot[] = Array.isArray(meta.slots) ? meta.slots : [];
  const height = figureHeight(meta.size_m);
  const picture = detail.files["thumb.jpg"];
  return (
    <>
      <InspectorHeader detail={detail} />
      <div className={INSPECTOR_GRID}>
        <div className="overflow-hidden rounded-xl bg-slate-100">
          {picture ? (
            <img
              src={picture}
              alt={name}
              className="block aspect-square w-full object-cover"
              data-testid="figure-picture"
            />
          ) : (
            <div className="aspect-square w-full" />
          )}
        </div>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2" data-testid="facts">
            <p className="m-0 text-sm font-semibold text-slate-800">
              {FIGURE_SENTENCE}
            </p>
            {height ? (
              <p className="m-0 text-sm text-slate-700">{height}</p>
            ) : null}
            {words.length > 0 ? (
              <p
                className="m-0 text-sm text-slate-700"
                data-testid="figure-words"
              >
                Chosen for: {words.join(", ")}
              </p>
            ) : null}
            <p
              className="m-0 text-sm text-slate-700"
              data-testid="figure-actions"
            >
              {ACTIONS_LINE}
            </p>
            <p
              className="m-0 text-sm text-slate-700"
              data-testid="figure-holds"
            >
              {HOLDS_LINE}
            </p>
          </section>
          <section className="flex flex-col gap-3">
            <h2 className="tb-h-row m-0">Colours</h2>
            <ul
              className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3"
              data-testid="figure-colours"
            >
              {slots.map((slot) => {
                const colour = colourOf(slot);
                return (
                  <li
                    key={slot.name}
                    className="flex min-h-[44px] items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5"
                  >
                    <span
                      aria-hidden="true"
                      className="h-6 w-6 shrink-0 rounded-full border border-slate-300"
                      style={colour ? { backgroundColor: colour } : undefined}
                      data-testid={`colour-${slot.name}`}
                      data-colour={colour ?? ""}
                    />
                    <span className="text-sm text-slate-800">
                      {SLOT_WORDS[slot.name] ?? slot.name}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="m-0 text-xs text-slate-600">{COLOURS_HELP}</p>
          </section>
        </div>
      </div>
      <UsedInAds detail={detail} />
    </>
  );
}

/** `/assets/figure/:id`: a figure of Objects' People. */
export function AssetFigurePage() {
  const { id = "" } = useParams();
  return (
    <Inspector kind="figure" id={id}>
      {(detail) => <FigureInspector key={detail.id} detail={detail} />}
    </Inspector>
  );
}
